import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { AddressBar, Counter, Marquee, Mast, StatusBar, xpCss, webCss } from 'lib/xp';

import pfp from 'assets/pfp.png';
import { EXPLORER } from 'data/msft';

// MISSION.exe — "onboard the internet." Milestone 1: 1% of the internet's 5.5B users =
// 55,000,000 holders. Pre-launch the counter reads 0 / awaiting launch. Post-launch
// golive.ts injects the CA and the holder count is read live from Blockscout (chain-derived,
// never hand-written). A holder-COUNT goal, never a price goal.
// 2026-09-13: a Y2K "space" page inside the XP frame (lib/xp `web` layer, purple variant).

const LIVE = true;
const CA = '0x88888cC1c17FfcBa563F418c61596b3fa2Ca2e2c';
const USERS_ONLINE = 5500000000; // ITU-scale estimate; the archive's own "5.5 billion frens"
const TARGET = 55000000; // 1%
const LADDER = [1000, 10000, 100000, 1000000, TARGET];

const fmt = n => (typeof n === 'number' && isFinite(n) ? n.toLocaleString('en-US') : '——');

export default function Mission() {
  const [holders, setHolders] = useState(null);
  const [at, setAt] = useState(null);

  useEffect(() => {
    if (!LIVE || CA === 'TBA') return undefined;
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${EXPLORER}/api/v2/tokens/${CA}`, { cache: 'no-store' });
        if (!r.ok) throw new Error(String(r.status));
        const j = await r.json();
        const h = Number(j.holders || j.holders_count || 0);
        if (alive && isFinite(h)) {
          setHolders(h);
          setAt(Date.now());
        }
      } catch (e) {
        /* keep last reading; NO SIGNAL is honest */
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const pct = holders ? Math.min(100, (holders / TARGET) * 100) : 0;
  const next = LADDER.find(l => (holders || 0) < l) || TARGET;
  const level = LADDER.findIndex(l => (holders || 0) < l);
  const blocks = 34;
  const lit = holders ? Math.max(1, Math.round((pct / 100) * blocks)) : 0;

  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">File</button>
        <button type="button">View</button>
        <button type="button">Favorites</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">Internet Coin</span>
      </div>
      <AddressBar path="internetcoin.vip/mission.htm" />
      <div className="xp__client web web--purple">
        <Mast
          logo={pfp}
          kicker="the mission"
          title="ONBOARD THE INTERNET"
          right={
            <>
              <span className="web__sticker web__sticker--gold">MILESTONE 1</span>
              <span className="mi__online">
                frens onboarded <Counter value={holders || 0} digits={8} />
              </span>
            </>
          }
        />
        <Marquee>
          ★ {fmt(USERS_ONLINE)} people are online ★ milestone 1 is one in a hundred of them: {fmt(TARGET)} frens holding the internet ★ this is a headcount goal, not a price goal ★ then the rest ★
        </Marquee>

        <div className="web__body">
          <div className="mi__q">what if we could onboard all of the internet?</div>
          <div className="mi__sub">
            {fmt(USERS_ONLINE)} people are online. milestone 1 is one in a hundred of them: <b>{fmt(TARGET)} frens</b> holding the internet. then the rest.
          </div>
          <div className="web__hr" />

          <div className="web__panel web__panel--purple">
            <div className="web__panel__t">Copying &apos;the internet&apos; to {fmt(TARGET)} computers…</div>
            <div className="mi__copybox">
              <div className="mi__copybox__anim">
                <span className="mi__doc">📄</span>
                <span className="mi__dots"><i /><i /><i /><i /><i /></span>
                <span className="mi__doc">🖥️</span>
              </div>
              <div className="mi__bar">
                {Array.from({ length: blocks }, (_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <i key={i} className={i < lit ? 'on' : ''} />
                ))}
              </div>
              <div className="mi__prog__foot">
                <span>
                  {LIVE ? (holders === null ? 'reading holders…' : `${fmt(holders)} of ${fmt(TARGET)} onboarded`) : `0 of ${fmt(TARGET)} · awaiting launch`}
                </span>
                <span className="web__led">{holders ? `${pct.toFixed(4)}%` : '0.0000%'}</span>
              </div>
              <div className="mi__dim">time remaining: as long as it takes</div>
            </div>
          </div>

          <div className="web__panel web__panel--white">
            <div className="web__panel__t">the ladder</div>
            <table className="web__table">
              <thead>
                <tr>
                  <th style={{ width: 24 }}> </th>
                  <th>level</th>
                  <th>frens</th>
                  <th>status</th>
                </tr>
              </thead>
              <tbody>
                {LADDER.map((l, i) => {
                  const done = (holders || 0) >= l;
                  const cur = !done && l === next;
                  return (
                    <tr className={done ? 'is-done' : cur ? 'is-next' : ''} key={l}>
                      <td>{done ? '☑' : '☐'}</td>
                      <td>level {i + 1}</td>
                      <td>{fmt(l)} frens</td>
                      <td>
                        {l === TARGET ? '1% of the internet' : done ? 'done' : ''}
                        {cur ? <span className="web__sticker web__sticker--gold mi__next">NEXT</span> : null}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td>☐</td>
                  <td>the dream</td>
                  <td>{fmt(USERS_ONLINE)}</td>
                  <td>all of it</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mi__foot">
            <span>
              {LIVE && CA !== 'TBA' ? (
                <a href={`${EXPLORER}/token/${CA}`} target="_blank" rel="noreferrer noopener">
                  holder count from Blockscout ↗{at ? ` · read ${new Date(at).toLocaleTimeString()}` : ''}
                </a>
              ) : (
                'holder count reads from the chain after launch · this is a headcount goal, not a price goal'
              )}
            </span>
            <span>level {Math.max(1, level < 0 ? LADDER.length : level + 1)} / {LADDER.length}</span>
          </div>
          <div className="web__foot">Best viewed in Internet Explorer 6 at 800×600 · © 2001 Internet Coin · 5.5 billion frens and counting</div>
        </div>
      </div>
      <StatusBar>
        <span>Done</span>
        <span>Internet</span>
      </StatusBar>
    </Div>
  );
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .55; }
`;
const fly = keyframes`
  0% { transform: translateX(-6px); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateX(46px); opacity: 0; }
`;

const Div = styled.div`
  ${xpCss};
  ${webCss};
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 0;
  }
  .mi__online {
    font-size: 10px;
    color: #f0e6ff;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }
  .mi__q {
    font: italic bold 15px ${"'Times New Roman', Times, Georgia, serif"};
    color: #2a0b52;
    margin-bottom: 4px;
  }
  .mi__sub {
    font-size: 11px;
    line-height: 1.55;
    color: #333;
  }
  /* the XP copy dialog: a document flies to the computer while the green blocks fill */
  .mi__copybox {
    padding: 4px 2px 0;
  }
  .mi__copybox__anim {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 18px;
  }
  .mi__dots {
    display: inline-flex;
    gap: 4px;
    position: relative;
    width: 54px;
    height: 14px;
    i {
      position: absolute;
      top: 3px;
      width: 8px;
      height: 8px;
      background: #fff;
      border: 1px solid #555;
      animation: ${fly} 1.8s linear infinite;
    }
    i:nth-child(2) { animation-delay: 0.36s; }
    i:nth-child(3) { animation-delay: 0.72s; }
    i:nth-child(4) { animation-delay: 1.08s; }
    i:nth-child(5) { animation-delay: 1.44s; }
  }
  .mi__bar {
    height: 22px;
    border: 1px solid #716f64;
    border-right-color: #fff;
    border-bottom-color: #fff;
    background: #fff;
    box-shadow: inset 1px 1px 0 rgba(0, 0, 0, 0.25);
    padding: 3px;
    display: flex;
    gap: 3px;
    i {
      flex: 1;
      background: #e3e0d2;
    }
    i.on {
      background: linear-gradient(180deg, #7ad37a, #3c9f3c 50%, #2f7d2f);
      animation: ${pulse} 2.4s ease-in-out infinite;
    }
  }
  .mi__prog__foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    font-size: 11px;
  }
  .mi__dim {
    margin-top: 6px;
    font-size: 10px;
    color: #6a6350;
    text-align: right;
  }
  /* done rows tint green in the layer; keep the frens number itself full ink so it stays legible */
  .web__table tr.is-done td:nth-child(3) {
    color: #1d1d1d;
    font-weight: bold;
  }
  .mi__next {
    margin-left: 8px;
    font-size: 11px;
    transform: rotate(-4deg);
  }
  .mi__foot {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 10px;
    color: #4a3d6b;
    line-height: 1.4;
    margin-top: 2px;
  }
  @media (max-width: 640px) {
    .web__mast__right {
      display: none;
    }
  }
`;
